/* ==========================================================================
   Sura header behaviour
   Registered as a custom element so it re-initialises automatically when the
   theme editor re-renders the section.
   ========================================================================== */

class SuraHeader extends HTMLElement {
  connectedCallback() {
    this.drawer = this.querySelector("[data-drawer]");
    this.overlay = this.querySelector("[data-drawer-overlay]");

    this.initDrawer();
    this.initAccordions();
    this.initMegaMenu();
    this.initDropdowns();
    this.initSticky();
    this.initPredictiveSearch();

    document.addEventListener("keydown", this.onKeydown);
  }

  disconnectedCallback() {
    document.removeEventListener("keydown", this.onKeydown);
    if (this.stickySentinelObserver) this.stickySentinelObserver.disconnect();
    document.body.classList.remove("sura-no-scroll");
  }

  onKeydown = (event) => {
    if (event.key !== "Escape") return;
    this.closeDrawer();
    this.closeMega();
    this.closeAllDropdowns();
    this.closeSearchResults();
  };

  /* ---------------------------------------------------------------- Drawer */

  initDrawer() {
    const openBtn = this.querySelector("[data-drawer-open]");
    const closeBtn = this.querySelector("[data-drawer-close]");
    if (!openBtn || !this.drawer) return;

    this.drawerTrigger = openBtn;
    openBtn.addEventListener("click", () => this.openDrawer());
    if (closeBtn) closeBtn.addEventListener("click", () => this.closeDrawer());
    if (this.overlay)
      this.overlay.addEventListener("click", () => this.closeDrawer());
  }

  openDrawer() {
    if (!this.drawer) return;
    this.drawer.hidden = false;
    if (this.overlay) this.overlay.hidden = false;
    // Next frame so the transition runs.
    requestAnimationFrame(() => this.drawer.classList.add("is-open"));
    this.drawerTrigger.setAttribute("aria-expanded", "true");
    document.body.classList.add("sura-no-scroll");

    const firstLink = this.drawer.querySelector("a, button");
    if (firstLink) firstLink.focus();
  }

  closeDrawer() {
    if (!this.drawer || this.drawer.hidden) return;
    this.drawer.classList.remove("is-open");
    this.drawerTrigger.setAttribute("aria-expanded", "false");
    document.body.classList.remove("sura-no-scroll");

    const hide = () => {
      this.drawer.hidden = true;
      if (this.overlay) this.overlay.hidden = true;
    };
    // Wait for the slide-out unless motion is reduced.
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      hide();
    } else {
      setTimeout(hide, 250);
    }
    this.drawerTrigger.focus();
  }

  /* ----------------------------------------------------- Drawer accordions */

  initAccordions() {
    this.querySelectorAll("[data-accordion-trigger]").forEach((trigger) => {
      trigger.addEventListener("click", () => {
        const panel = trigger.nextElementSibling;
        if (!panel) return;
        const isOpen = trigger.getAttribute("aria-expanded") === "true";
        trigger.setAttribute("aria-expanded", String(!isOpen));
        panel.hidden = isOpen;
      });
    });
  }

  /* ------------------------------------------------------------- Mega menu */

  initMegaMenu() {
    this.megaTrigger = this.querySelector("[data-mega-trigger]");
    this.megaPanel = this.querySelector("[data-mega-panel]");
    if (!this.megaTrigger || !this.megaPanel) return;

    this.megaTrigger.addEventListener("click", (event) => {
      event.stopPropagation();
      const isOpen = this.megaTrigger.getAttribute("aria-expanded") === "true";
      isOpen ? this.closeMega() : this.openMega();
    });

    document.addEventListener("click", (event) => {
      if (!this.contains(event.target)) this.closeMega();
    });

    this.megaPanel.addEventListener("click", (event) =>
      event.stopPropagation(),
    );
  }

  openMega() {
    this.closeAllDropdowns();
    this.megaPanel.hidden = false;
    this.megaTrigger.setAttribute("aria-expanded", "true");
  }

  closeMega() {
    if (!this.megaPanel || this.megaPanel.hidden) return;
    this.megaPanel.hidden = true;
    this.megaTrigger.setAttribute("aria-expanded", "false");
  }

  /* ------------------------------------------------------- Nav dropdowns   */

  initDropdowns() {
    this.dropdownTriggers = Array.from(
      this.querySelectorAll("[data-dropdown-trigger]"),
    );

    this.dropdownTriggers.forEach((trigger) => {
      const panel = trigger.nextElementSibling;
      if (!panel) return;
      const item = trigger.closest(".sura-nav__item");

      trigger.addEventListener("click", (event) => {
        event.stopPropagation();
        const isOpen = trigger.getAttribute("aria-expanded") === "true";
        this.closeAllDropdowns();
        this.closeMega();
        if (!isOpen) {
          trigger.setAttribute("aria-expanded", "true");
          panel.hidden = false;
        }
      });

      // Hover on pointer devices, without breaking touch or keyboard.
      if (item && window.matchMedia("(hover: hover)").matches) {
        item.addEventListener("mouseenter", () => {
          this.closeAllDropdowns();
          trigger.setAttribute("aria-expanded", "true");
          panel.hidden = false;
        });
        item.addEventListener("mouseleave", () => {
          trigger.setAttribute("aria-expanded", "false");
          panel.hidden = true;
        });
      }
    });

    document.addEventListener("click", (event) => {
      if (!this.contains(event.target)) this.closeAllDropdowns();
    });
  }

  closeAllDropdowns() {
    if (!this.dropdownTriggers) return;
    this.dropdownTriggers.forEach((trigger) => {
      trigger.setAttribute("aria-expanded", "false");
      if (trigger.nextElementSibling) trigger.nextElementSibling.hidden = true;
    });
  }

  /* ---------------------------------------------------------------- Sticky */

  initSticky() {
    if (!this.classList.contains("sura-header--sticky")) return;

    // A zero-height sentinel above the header tells us when it leaves the top.
    const sentinel = document.createElement("div");
    sentinel.setAttribute("aria-hidden", "true");
    sentinel.style.cssText = "position:absolute;height:1px;width:1px;";
    this.parentNode.insertBefore(sentinel, this);

    this.stickySentinelObserver = new IntersectionObserver(
      ([entry]) => this.classList.toggle("is-stuck", !entry.isIntersecting),
      { threshold: 0 },
    );
    this.stickySentinelObserver.observe(sentinel);
  }

  /* ---------------------------------------------------- Predictive search  */

  initPredictiveSearch() {
    if (this.dataset.predictiveSearch !== "true") return;

    this.searchInput = this.querySelector("[data-search-input]");
    this.searchResults = this.querySelector("[data-search-results]");
    if (!this.searchInput || !this.searchResults) return;

    let timer;
    this.searchInput.addEventListener("input", () => {
      clearTimeout(timer);
      const term = this.searchInput.value.trim();
      if (term.length < 3) {
        this.closeSearchResults();
        return;
      }
      timer = setTimeout(() => this.fetchSuggestions(term), 300);
    });

    document.addEventListener("click", (event) => {
      if (!this.contains(event.target)) this.closeSearchResults();
    });
  }

  async fetchSuggestions(term) {
    const url =
      `${window.Shopify.routes.root}search/suggest.json` +
      `?q=${encodeURIComponent(term)}` +
      "&resources[type]=product,collection" +
      "&resources[limit]=6";

    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error(response.status);
      const data = await response.json();
      this.renderSuggestions(data.resources.results);
    } catch (error) {
      // Silent failure: the form still submits normally to /search.
      this.closeSearchResults();
    }
  }

  renderSuggestions(results) {
    const products = results.products || [];
    const collections = results.collections || [];

    if (products.length === 0 && collections.length === 0) {
      this.searchResults.innerHTML =
        '<p class="sura-search__empty">No matches yet. Press enter to search everything.</p>';
      this.openSearchResults();
      return;
    }

    const productMarkup = products
      .map((product) => {
        const image =
          product.featured_image && product.featured_image.url
            ? `<img src="${product.featured_image.url}" alt="" loading="lazy" width="40" height="48">`
            : "";
        return `
          <a class="sura-search__result" href="${product.url}" role="option">
            ${image}
            <span>
              <span class="sura-search__result-title">${product.title}</span>
              <span class="sura-search__result-price">${product.price ? this.formatMoney(product.price) : ""}</span>
            </span>
          </a>`;
      })
      .join("");

    const collectionMarkup = collections
      .map(
        (collection) => `
          <a class="sura-search__result" href="${collection.url}" role="option">
            <span class="sura-search__result-title">${collection.title}</span>
          </a>`,
      )
      .join("");

    this.searchResults.innerHTML = collectionMarkup + productMarkup;
    this.openSearchResults();
  }

  formatMoney(price) {
    // suggest.json returns a preformatted string on most themes; fall back safely.
    return typeof price === "string" ? price : `₹${(price / 100).toFixed(2)}`;
  }

  openSearchResults() {
    this.searchResults.hidden = false;
    this.searchInput.setAttribute("aria-expanded", "true");
  }

  closeSearchResults() {
    if (!this.searchResults) return;
    this.searchResults.hidden = true;
    this.searchInput.setAttribute("aria-expanded", "false");
  }
}

if (!customElements.get("sura-header")) {
  customElements.define("sura-header", SuraHeader);
}
